<template>
  <div>
    <!-- Load error -->
    <b-alert v-if="loadError" variant="danger" show>
      {{ $t('company-page.service-prices.load-error') }}
      <b-button
        size="sm"
        variant="outline-danger"
        class="ml-2"
        @click="loadServices"
      >
        {{ $t('company-page.service-prices.retry') }}
      </b-button>
    </b-alert>

    <!-- Export toolbar -->
    <div class="d-flex flex-wrap align-items-center mb-3" style="gap: 0.5rem">
      <b-button
        size="sm"
        variant="outline-secondary"
        @click="exportPdfPricelist"
      >
        {{ $t('company-page.service-prices.export-pdf-pricelist') }}
      </b-button>
      <b-button
        size="sm"
        variant="outline-secondary"
        @click="exportPdfPackages"
      >
        {{ $t('company-page.service-prices.export-pdf-packages') }}
      </b-button>
      <b-button size="sm" variant="outline-secondary" @click="exportCsv">
        {{ $t('company-page.service-prices.export-csv') }}
      </b-button>
      <b-button
        size="sm"
        variant="outline-secondary"
        @click="$refs.csvImportInput.click()"
      >
        {{ $t('company-page.service-prices.import-csv') }}
      </b-button>
      <input
        ref="csvImportInput"
        type="file"
        accept=".csv"
        class="d-none"
        @change="importCsv"
      />
      <b-form-checkbox v-model="withoutPrices" class="ml-2 mb-0">
        {{ $t('company-page.service-prices.without-prices') }}
      </b-form-checkbox>
    </div>

    <!-- Filters row -->
    <b-row class="mb-2">
      <b-col md="5">
        <b-input
          v-model="filter"
          type="search"
          :placeholder="$t('company-page.service-prices.filter')"
        />
      </b-col>
      <b-col md="3">
        <b-form-select v-model="brandFilter" :options="brandOptions" />
      </b-col>
      <b-col md="4" class="d-flex align-items-center">
        <b-form-checkbox v-model="groupByPackages" class="mb-0">
          {{ $t('company-page.service-prices.group-by-packages') }}
        </b-form-checkbox>
      </b-col>
    </b-row>

    <!-- Grouped view -->
    <template v-if="groupByPackages">
      <div v-for="group in servicesGrouped" :key="group.label" class="mb-4">
        <div
          class="bg-light border-bottom border-top px-3 py-2 font-weight-bold text-secondary small"
        >
          {{ group.label }}
        </div>
        <b-table
          :items="group.items"
          :fields="fields"
          sort-by="descriptionDe"
          sort-direction="asc"
          stacked="md"
        >
          <template #cell(descriptionDe)="{ item }">
            <div>{{ item.descriptionDe }}</div>
            <small
              v-if="item.hintsDe"
              class="text-muted d-block"
              v-html="item.hintsDe"
            ></small>
          </template>
          <template #cell(pricePurchasing)="{ value }">
            <span v-if="!withoutPrices">{{ value | currency }}</span>
          </template>
          <template #cell(accountService.pricePurchasing)="{ item }">
            <template v-if="!withoutPrices">
              <div class="d-flex align-items-center">
                <b-badge
                  v-if="item.accountService.status === 'pending_increase'"
                  variant="warning"
                  class="mr-1"
                >
                  {{ $t('company-page.service-prices.status-pending') }}
                </b-badge>
                <b-badge
                  v-if="item.accountService.status === 'declined'"
                  variant="danger"
                  class="mr-1"
                >
                  {{ $t('company-page.service-prices.status-declined') }}
                </b-badge>
                <div>
                  <b-input
                    v-model="item.accountService.pricePurchasing"
                    @change="priceChanged(item)"
                    :placeholder="
                      item.pricePurchasing
                        ? Number(item.pricePurchasing).toFixed(2)
                        : ''
                    "
                    class="text-right"
                    :state="priceValidationState(item)"
                    :id="`price-g-${item.id}`"
                  />
                  <small
                    v-if="priceValidationState(item) === false"
                    class="text-danger d-block"
                  >
                    {{ $t('company-page.service-prices.price-min-error') }}
                    {{ (item.pricePurchasing * 0.5) | currency }}
                  </small>
                  <small
                    v-if="isPriceIncreaseRequest(item)"
                    class="text-info d-block"
                  >
                    {{ $t('company-page.service-prices.price-request-hint') }}
                  </small>
                  <small
                    v-if="
                      item.accountService.status === 'pending_increase' &&
                      item.accountService.requestedPrice
                    "
                    class="text-warning d-block"
                  >
                    {{ $t('company-page.service-prices.requested-price') }}:
                    {{ item.accountService.requestedPrice | currency }}
                  </small>
                </div>
              </div>
            </template>
          </template>
        </b-table>
      </div>
    </template>

    <!-- Normal (flat) view -->
    <template v-else>
      <b-table
        :items="servicesFiltered"
        :fields="fields"
        :per-page="perPage"
        :current-page="currentPage"
        sort-by="descriptionDe"
        sort-direction="asc"
        stacked="md"
      >
        <template #cell(descriptionDe)="{ item }">
          <div>{{ item.descriptionDe }}</div>
          <small
            v-if="item.hintsDe"
            class="text-muted d-block"
            v-html="item.hintsDe"
          ></small>
        </template>
        <template #cell(pricePurchasing)="{ value }">
          <span v-if="!withoutPrices">{{ value | currency }}</span>
        </template>
        <template #cell(accountService.pricePurchasing)="{ item }">
          <div class="d-flex align-items-center">
            <b-badge
              v-if="item.accountService.status === 'pending_increase'"
              variant="warning"
              class="mr-1"
            >
              {{ $t('company-page.service-prices.status-pending') }}
            </b-badge>
            <b-badge
              v-if="item.accountService.status === 'declined'"
              variant="danger"
              class="mr-1"
            >
              {{ $t('company-page.service-prices.status-declined') }}
            </b-badge>
            <div>
              <b-input
                v-model="item.accountService.pricePurchasing"
                @change="priceChanged(item)"
                :placeholder="
                  item.pricePurchasing
                    ? Number(item.pricePurchasing).toFixed(2)
                    : ''
                "
                class="text-right"
                :state="priceValidationState(item)"
                :id="`price-${item.id}`"
              />
              <small
                v-if="priceValidationState(item) === false"
                class="text-danger d-block"
              >
                {{ $t('company-page.service-prices.price-min-error') }}
                {{ (item.pricePurchasing * 0.5) | currency }}
              </small>
              <small
                v-if="isPriceIncreaseRequest(item)"
                class="text-info d-block"
              >
                {{ $t('company-page.service-prices.price-request-hint') }}
              </small>
              <small
                v-if="
                  item.accountService.status === 'pending_increase' &&
                  item.accountService.requestedPrice
                "
                class="text-warning d-block"
              >
                {{ $t('company-page.service-prices.requested-price') }}:
                {{ item.accountService.requestedPrice | currency }}
              </small>
            </div>
          </div>
        </template>
      </b-table>
      <b-pagination
        :total-rows="servicesFiltered.length"
        :per-page="perPage"
        v-model="currentPage"
        align="center"
        first-number
        last-number
      />
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const TEST_PREFIX = '[engineering-test]';

export default {
  data() {
    return {
      services: [],
      fields: [
        {
          key: 'descriptionDe',
          label: this.$t('company-page.service-prices.name'),
          sortable: true,
          tdClass: 'align-middle',
        },
        {
          key: 'serviceType',
          label: this.$t('company-page.service-prices.type'),
          thClass: 'text-nowrap',
          tdClass: 'align-middle',
        },
        {
          key: 'unitMeasure',
          label: this.$t('company-page.service-prices.unit-measure'),
          thClass: 'text-nowrap',
          tdClass: 'align-middle',
        },
        {
          key: 'pricePurchasing',
          label: this.$t('company-page.service-prices.original-price'),
          class: 'text-right text-nowrap',
          tdClass: 'align-middle',
        },
        {
          key: 'accountService.pricePurchasing',
          label: this.$t('company-page.service-prices.my-price'),
          class: 'text-right text-nowrap',
          tdClass: 'align-middle',
          thStyle: 'width: 12em',
        },
      ],
      perPage: 20,
      currentPage: 1,
      filter: null,
      brandFilter: null,
      groupByPackages: false,
      withoutPrices: false,
      loading: false,
      loadError: false,
    };
  },
  computed: {
    servicesBase() {
      return this.services.filter(
        (item) =>
          !item.name?.startsWith(TEST_PREFIX) &&
          !item.descriptionDe?.startsWith(TEST_PREFIX),
      );
    },
    servicesFiltered() {
      const filterLower = this.filter?.toLowerCase();
      let result = this.servicesBase;

      if (this.brandFilter) {
        result = result.filter((item) =>
          (item.brands ?? []).includes(this.brandFilter),
        );
      }

      if (!filterLower) return result;

      return result.filter((item) =>
        [
          item.name,
          item.descriptionDe,
          item.descriptionEn,
          item.hintsDe,
          item.hintsEn,
        ]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(filterLower)),
      );
    },
    servicesGrouped() {
      const groups = new Map();
      for (const svc of this.servicesFiltered) {
        const pkgs = svc.packages?.length ? svc.packages : [null];
        for (const pkg of pkgs) {
          const key = pkg ?? '__none__';
          if (!groups.has(key)) {
            groups.set(key, {
              label: pkg ?? this.$t('company-page.service-prices.no-package'),
              items: [],
            });
          }
          groups.get(key).items.push(svc);
        }
      }
      return [...groups.values()].sort((a, b) => {
        if (a.label === this.$t('company-page.service-prices.no-package'))
          return 1;
        if (b.label === this.$t('company-page.service-prices.no-package'))
          return -1;
        return a.label.localeCompare(b.label);
      });
    },
    brandOptions() {
      const brands = new Set();
      for (const svc of this.servicesBase) {
        for (const b of svc.brands ?? []) brands.add(b);
      }
      return [
        {
          value: null,
          text: this.$t('company-page.service-prices.brand-filter-all'),
        },
        ...[...brands].sort().map((b) => ({ value: b, text: b })),
      ];
    },
  },
  methods: {
    async loadServices() {
      this.loading = true;
      this.loadError = false;
      try {
        const { data } = await axios.get('/data-api/account-service/matching');
        this.services = data.map((s) => {
          const as = s.accountServices?.[0] ?? null;
          return {
            ...s,
            accountService: as
              ? {
                  ...as,
                  pricePurchasing:
                    as.pricePurchasing != null
                      ? parseFloat(as.pricePurchasing).toFixed(2)
                      : null,
                }
              : {
                  id: null,
                  pricePurchasing: null,
                  status: 'active',
                  requestedPrice: null,
                },
          };
        });
      } catch (error) {
        this.loadError = true;
        this.$bvToast.toast(error.response?.data?.message ?? error.message, {
          title: this.$t('company-page.service-prices.load-error'),
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.loading = false;
      }
    },

    isPriceIncreaseRequest(item) {
      const price = parseFloat(item.accountService.pricePurchasing);
      return (
        !isNaN(price) &&
        item.pricePurchasing > 0 &&
        price > Math.round(item.pricePurchasing * 110) / 100
      );
    },

    priceValidationState(item) {
      const price = parseFloat(item.accountService.pricePurchasing);
      if (!price && price !== 0) return null;
      return price < Math.round(item.pricePurchasing * 50) / 100 ? false : null;
    },

    async priceChanged(service) {
      let normalizedValue = String(
        service.accountService.pricePurchasing ?? '',
      ).replace(',', '.');
      normalizedValue = parseFloat(normalizedValue);
      if (isNaN(normalizedValue)) normalizedValue = null;

      if (
        normalizedValue !== null &&
        this.priceValidationState(service) === false
      )
        return;

      service.accountService.pricePurchasing = normalizedValue;
      try {
        await this.applyChange(service);
        if (service.accountService.pricePurchasing != null) {
          service.accountService.pricePurchasing = parseFloat(
            service.accountService.pricePurchasing,
          ).toFixed(2);
        }
        const isPending = service.accountService.status === 'pending_increase';
        this.$bvToast.toast(`${service.descriptionDe}`, {
          title: isPending
            ? this.$t('company-page.service-prices.request-submitted')
            : this.$t('company-page.service-prices.save-success'),
          variant: isPending ? 'info' : 'success',
          solid: true,
        });
      } catch (error) {
        this.$bvToast.toast(
          `${service.descriptionDe}: ${
            error.response?.data?.message ?? error.message
          }`,
          {
            title: this.$t('company-page.service-prices.save-error'),
            variant: 'danger',
            solid: true,
          },
        );
      }
    },

    async applyChange(service) {
      const as = service.accountService;
      const endpoint = `/data-api/account-service`;
      if (as.pricePurchasing !== null && as.pricePurchasing !== undefined) {
        let result;
        if (as.id) {
          const { data } = await axios.patch(`${endpoint}/${as.id}`, {
            pricePurchasing: as.pricePurchasing,
          });
          result = data;
        } else {
          const { data } = await axios.post(endpoint, {
            pricePurchasing: as.pricePurchasing,
            service: { id: service.id },
          });
          result = data;
          as.id = data.id;
        }
        as.status = result.status ?? 'active';
        as.requestedPrice = result.requestedPrice ?? null;
      } else if (as.id) {
        await axios.delete(`${endpoint}/${as.id}`);
        as.id = null;
        as.status = 'active';
        as.requestedPrice = null;
      }
    },

    // --- Exports ---

    exportPdfPricelist() {
      const doc = new jsPDF();
      const rows = this.servicesFiltered;
      const head = [
        [
          this.$t('company-page.service-prices.name'),
          this.$t('company-page.service-prices.type'),
          this.$t('company-page.service-prices.unit-measure'),
          ...(this.withoutPrices
            ? []
            : [
                this.$t('company-page.service-prices.original-price'),
                this.$t('company-page.service-prices.my-price'),
              ]),
        ],
      ];

      // Group by serviceFamily (topic)
      const topics = new Map();
      for (const svc of rows) {
        const topic = svc.serviceFamily ?? '';
        if (!topics.has(topic)) topics.set(topic, []);
        topics.get(topic).push(svc);
      }

      let startY = 14;
      doc.setFontSize(12);
      doc.text(
        this.$t('company-page.service-prices.export-pdf-pricelist'),
        14,
        startY,
      );
      startY += 8;

      for (const [topic, items] of topics) {
        if (topic) {
          doc.setFontSize(10);
          doc.text(topic, 14, startY);
          startY += 4;
        }
        doc.autoTable({
          startY,
          head,
          body: items.map((s) => [
            s.descriptionDe ?? s.name,
            s.serviceType ?? '',
            s.unitMeasure ?? '',
            ...(this.withoutPrices
              ? []
              : [
                  s.pricePurchasing != null
                    ? `€ ${Number(s.pricePurchasing).toFixed(2)}`
                    : '',
                  s.accountService?.pricePurchasing != null
                    ? `€ ${Number(s.accountService.pricePurchasing).toFixed(2)}`
                    : '',
                ]),
          ]),
          styles: { fontSize: 8 },
          margin: { left: 14, right: 14 },
        });
        startY = doc.lastAutoTable.finalY + 6;
      }

      doc.save('pricelist.pdf');
    },

    exportPdfPackages() {
      const doc = new jsPDF();
      doc.setFontSize(12);
      doc.text(
        this.$t('company-page.service-prices.export-pdf-packages'),
        14,
        14,
      );

      const groups = this.servicesGrouped.filter(
        (g) => g.label !== this.$t('company-page.service-prices.no-package'),
      );

      let startY = 22;
      for (const group of groups) {
        doc.setFontSize(10);
        doc.text(group.label, 14, startY);
        startY += 4;
        doc.autoTable({
          startY,
          head: [
            [
              this.$t('company-page.service-prices.name'),
              this.$t('company-page.service-prices.type'),
            ],
          ],
          body: group.items.map((s) => [
            s.descriptionDe ?? s.name,
            s.serviceType ?? '',
          ]),
          styles: { fontSize: 8 },
          margin: { left: 14, right: 14 },
        });
        startY = doc.lastAutoTable.finalY + 6;
      }

      doc.save('package-overview.pdf');
    },

    exportCsv() {
      const rows = this.servicesFiltered;
      const cols = [
        'id',
        'name',
        'descriptionDe',
        'serviceType',
        'unitMeasure',
        ...(this.withoutPrices ? [] : ['pricePurchasing', 'myPrice']),
      ];
      const lines = [
        cols.join(';'),
        ...rows.map((s) =>
          [
            s.id,
            s.name,
            s.descriptionDe,
            s.serviceType ?? '',
            s.unitMeasure ?? '',
            ...(this.withoutPrices
              ? []
              : [
                  s.pricePurchasing ?? '',
                  s.accountService?.pricePurchasing ?? '',
                ]),
          ]
            .map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`)
            .join(';'),
        ),
      ];
      const blob = new Blob([lines.join('\r\n')], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'services.csv';
      a.click();
      URL.revokeObjectURL(url);
    },

    async importCsv(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter(Boolean);
      const delimiter = lines[0].includes(';') ? ';' : ',';
      const header = lines[0]
        .split(delimiter)
        .map((h) => h.replace(/^"|"$/g, '').trim());
      const idCol = header.indexOf('id');
      const priceCol = header.indexOf('myPrice');
      if (idCol === -1 || priceCol === -1) {
        this.$bvToast.toast('CSV must have "id" and "myPrice" columns', {
          title: 'Import error',
          variant: 'danger',
          solid: true,
        });
        return;
      }
      let ok = 0;
      let fail = 0;
      for (const line of lines.slice(1)) {
        const cols = line
          .split(delimiter)
          .map((c) => c.replace(/^"|"$/g, '').trim());
        const serviceId = cols[idCol];
        const price = parseFloat(cols[priceCol]);
        if (!serviceId || isNaN(price)) continue;
        const svc = this.services.find((s) => s.id === serviceId);
        if (!svc) continue;
        svc.accountService.pricePurchasing = price;
        try {
          await this.applyChange(svc);
          ok++;
        } catch {
          fail++;
        }
      }
      this.$bvToast.toast(`Imported: ${ok} ok, ${fail} failed`, {
        title: 'CSV Import',
        variant: fail ? 'warning' : 'success',
        solid: true,
      });
      event.target.value = '';
    },
  },
  created() {
    this.loadServices();
  },
};
</script>
